import { useTacticHooks } from "../providers/TacticHooksProvider";
import { QueryOptions } from "./types";
import { ScheduleParamsAll } from "@gettactic/api";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import { useMemo } from "react";

type WorkingLocationsPage = {
  days: unknown;
  cursor?: string | null;
};

export function useWorkingLocations(
  filters: ScheduleParamsAll,
  options: QueryOptions = { keepPreviousData: true, enabled: true },
  key = ""
) {
  const { apiClient } = useTacticHooks();

  // Standardize the filters to prevent changes in hook calls
  const standardizedFilters = useMemo(() => {
    return {
      ...filters,
      limit: filters.limit || 100
    };
  }, [JSON.stringify(filters)]);

  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery<WorkingLocationsPage, Error>({
    queryKey: [key, "schedules", "workingLocations", standardizedFilters],
    queryFn: async ({ pageParam: cursor }) => {
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const queryFilters: any = { ...standardizedFilters };
      if (cursor != null) queryFilters.cursor = cursor;

      try {
        const res = await apiClient.schedules.workingLocations(queryFilters);

        // biome-ignore lint/suspicious/noExplicitAny: <explanation>
        const result = res?.result as any;

        return {
          days: result?.days ?? {},
          cursor: result?.cursor ?? null
        } satisfies WorkingLocationsPage;
      } catch {
        return { days: {}, cursor: null } satisfies WorkingLocationsPage;
      }
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      const all = lastPage?.days ?? {};
      const days = Object.keys(all);
      let maxLength = 0;
      const lastCursor = lastPage?.cursor;

      for (let i = 0; i < days.length; i++) {
        const day = days[i];
        const dayData = [].concat(all[day as keyof typeof all] ?? []);
        const dayLength = dayData.length;
        if (dayLength > maxLength) {
          maxLength = dayLength;
        }
      }
      return maxLength < (standardizedFilters.limit ?? 0)
        ? undefined
        : (lastCursor ?? undefined);
    },
    placeholderData: options.keepPreviousData ? keepPreviousData : undefined
  });

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  };
}
